import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useApi } from '../api/context';
import { isTerminalRunStatus, type Run } from '../api/types';
import { initialEventLog, type EventLogState } from '../lib/eventLog';
import { runEventStreams, type RunLogSnapshot, type StreamState } from './runEventStreams';

export type { StreamState } from './runEventStreams';

const IDLE: RunLogSnapshot = {
  log: initialEventLog,
  stream: 'idle',
  error: null,
  completed: null,
};

/**
 * The events of a run through the shared subscription (spec 6.5, 6.8.12): terminal runs are loaded with the
 * JSON form, other runs follow the Server-Sent Events stream until `run-completed`, which is passed to
 * `onCompleted`. Every view of the same run shares one stream.
 */
export function useRunEventLog(
  runId: string | null | undefined,
  run: Run | undefined,
  onCompleted?: (run: Run) => void,
): { log: EventLogState; stream: StreamState; error: unknown } {
  const api = useApi();
  const streams = runEventStreams(api);
  const onCompletedRef = useRef(onCompleted);
  const terminalRef = useRef(run ? isTerminalRunStatus(run.status) : false);
  useEffect(() => {
    onCompletedRef.current = onCompleted;
    terminalRef.current = run ? isTerminalRunStatus(run.status) : false;
  });

  const ready = !!runId && !!run;
  const subscribe = useCallback(
    (listener: () => void) => {
      if (!ready || !runId) return () => {};
      return streams.subscribe(runId, terminalRef.current, listener, (completed) =>
        onCompletedRef.current?.(completed),
      );
    },
    [streams, runId, ready],
  );
  const snapshot = useSyncExternalStore(
    subscribe,
    () => (ready && runId ? streams.snapshot(runId) : IDLE),
    () => IDLE,
  );
  return { log: snapshot.log, stream: snapshot.stream, error: snapshot.error };
}
